import { AxiosInstance } from 'axios';
import ApiClient from './ApiClient';
import {
  CommunicationTemplate,
  CommunicationTemplateUpdate,
  CommunicationEventType,
  RevertTemplateRequest,
} from '../types/Communication';

// Mentoring Fusion API base URL
const API_URL = process.env.REACT_APP_MENTORING_API_URL ?? '';

class CommunicationService {
  private apiClient: AxiosInstance;

  constructor() {
    this.apiClient = new ApiClient(API_URL).getClient();
  }

  /* -------------------------------------------------------------------------- */
  /*                             Template Endpoints                              */
  /* -------------------------------------------------------------------------- */

  public listTemplates = async (
    organizationId: string,
    campaignId: string
  ): Promise<CommunicationTemplate[]> => {
    type ApiTemplate = {
      subject: string;
      body: string;
      is_enabled: boolean;
      id: string;
      campaign_id: string;
      communication_event_type: string;
      created_at: string;
      updated_at: string;
    };

    const response = await this.apiClient.get<ApiTemplate[]>(
      `/organizations/${organizationId}/campaigns/${campaignId}/communication/templates`
    );

    // Convert snake_case fields → camelCase + normalize event type to enum format
    const toEnum = (et: string): CommunicationEventType => {
      switch (et.toLowerCase()) {
        case 'user_submits_application':
          return 'USER_SUBMITS_APPLICATION';
        case 'user_is_matched':
          return 'USER_IS_MATCHED';
        case 'user_is_rematched':
          return 'USER_IS_REMATCHED';
        case 'feedback_is_requested':
          return 'FEEDBACK_IS_REQUESTED';
        case 'weekly_check_in':
          return 'WEEKLY_CHECK_IN';
        case 'user_is_not_matched':
          return 'USER_IS_NOT_MATCHED';
        default:
          // Fallback – cast as any to satisfy type but still return string
          return 'USER_SUBMITS_APPLICATION';
      }
    };

    const camelTemplates: CommunicationTemplate[] = response.data.map((t) => ({
      id: t.id,
      campaignId: t.campaign_id,
      eventType: toEnum(t.communication_event_type),
      subject: t.subject,
      body: t.body,
      isEnabled: t.is_enabled,
      createdAt: t.created_at,
      updatedAt: t.updated_at,
    }));

    return camelTemplates;
  };

  public updateTemplate = async (
    organizationId: string,
    campaignId: string,
    templateId: string,
    updates: CommunicationTemplateUpdate
  ): Promise<CommunicationTemplate> => {
    // Map camelCase to snake_case expected by backend
    const payload: Record<string, unknown> = {};
    if (updates.subject !== undefined) payload['subject'] = updates.subject;
    if (updates.body !== undefined) payload['body'] = updates.body;
    if (updates.isEnabled !== undefined) payload['is_enabled'] = updates.isEnabled;

    const response = await this.apiClient.put<CommunicationTemplate>(
      `/organizations/${organizationId}/campaigns/${campaignId}/communication/templates/${templateId}`,
      payload
    );
    return response.data;
  };

  public revertTemplate = async (
    organizationId: string,
    campaignId: string,
    templateType: CommunicationEventType
  ): Promise<CommunicationTemplate> => {
    const payload: RevertTemplateRequest = { templateType };
    const response = await this.apiClient.post<CommunicationTemplate>(
      `/organizations/${organizationId}/campaigns/${campaignId}/communication/templates/revert`,
      payload
    );
    return response.data;
  };
}

const communicationService = new CommunicationService();
export default communicationService;
